import * as React from 'react';

import { DateTime } from 'luxon';
import { capitalize, lowerCase, startCase } from 'lodash';

import { Table } from '@shared/components/bootstrap';

import { Permission } from '@admin/components/helpers/permission';
import { TeachInWarning } from '@admin/components/logistics/skus/teach_in_warning';
import { Timestamp } from '@admin/components/timestamp';
import { logisticsDetailedLoadURL, logisticsEditLoadURL } from '@admin/config/routes';
import { LOGISTICS } from '@admin/libraries/access_control_catalog.generated';
import { PaginatedLoadQuery, Logistics__SchedulingOptionEnum } from '@admin/schema';

const buttonPadding = { margin: 1 };

type LoadType = PaginatedLoadQuery['paginated']['results'][number];

const formatDate = (date: string) => DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED);

const ExpectationsSummary: React.FC<{ load: LoadType }> = ({ load }) => {
  const customerExpectations = new Map<
    string,
    { customerName: string; totalQuantities: number; skuValues: string[]; requiresTeachIn: boolean }
  >();
  load.fulfillmentExpectations.forEach((fe) => {
    const customerName = fe.sku.account.customer.name;
    const accountID = fe.sku.account.id;
    const customerAggregate = customerExpectations.get(accountID);
    if (customerAggregate) {
      customerAggregate.totalQuantities += fe.quantity;
      customerAggregate.skuValues.push(fe.sku.value);
    } else {
      customerExpectations.set(accountID, {
        customerName: customerName,
        totalQuantities: fe.quantity,
        skuValues: [fe.sku.value],
        requiresTeachIn: load.requiresTeachIn,
      });
    }
  });

  return (
    <>
      {Array.from(customerExpectations.keys()).map((accountID) => (
        <span key={accountID}>
          <strong>
            {customerExpectations.get(accountID)?.customerName}: {customerExpectations.get(accountID)?.totalQuantities}{' '}
            items | {customerExpectations.get(accountID)?.skuValues.length} SKUs
            {customerExpectations.get(accountID)?.requiresTeachIn && (
              <>
                {' '}
                <TeachInWarning />
              </>
            )}
          </strong>
          <br />
          <small>{customerExpectations.get(accountID)?.skuValues.join(', ')}</small>
          <br />
        </span>
      ))}
    </>
  );
};

const getLoadStateBadgeColor = (state: string): string => {
  switch (state) {
    case 'draft':
      return 'bg-secondary';
    case 'pending':
    case 'prepared':
      return 'bg-info';
    case 'completed':
      return 'bg-success';
    case 'canceled':
    default:
      return 'bg-danger';
  }
};

export const LoadsTable: React.FC<{
  loads: LoadType[];
  dateHeader: string;
}> = ({ loads, dateHeader }) => (
  <Table striped responsive>
    <thead>
      <tr className="load-list__row">
        <th className="col-sm-1 text-left">Type</th>
        <th className="col-sm-1 text-left">Load Number</th>
        <th className="col-sm-1 text-center">State</th>
        <th className="col-sm-1 text-left">Warehouse</th>
        <th className="col-sm-2 text-left">{dateHeader}</th>
        <th className="col-sm-3 text-left">Expectations</th>
        <th className="col-sm-1 text-left">Carrier Name</th>
        <th className="col-sm-2 text-left" />
      </tr>
    </thead>
    <tbody>
      <>
        {loads.map((load) => (
          <tr className="load-list__row" key={load.id}>
            <td className="col-sm-1 text-left">{load.type}</td>
            <td className="col-sm-1 text-left">{load.number}</td>
            <td className="col-sm-1 text-center">
              <div className={`badge ${getLoadStateBadgeColor(load.state)}`}>{capitalize(load.state)}</div>
              {load.requiresAgentReview && (
                <div style={{ marginTop: '8px' }} className={`badge bg-warning`}>
                  Review Required
                </div>
              )}
            </td>
            <td className="col-sm-1 text-left">{load.warehouse.name}</td>
            <td className="col-sm-2 text-left">
              {load.appointmentDate ? (
                <Timestamp value={load.appointmentDate} timezone={load.warehouse.tz} />
              ) : (
                <>
                  {(load.schedulingOption === Logistics__SchedulingOptionEnum.SpecificDate ||
                    !load.schedulingOption) && (
                    <span>
                      <strong>Request:</strong> {formatDate(load.requestedAppointmentDate)} -{' '}
                      {startCase(lowerCase(load.requestedAppointmentWindow))}
                    </span>
                  )}
                  {load.schedulingOption === Logistics__SchedulingOptionEnum.CarrierWillContactClutter &&
                    'Carrier will reach out to Clutter to arrange a date'}
                  {load.schedulingOption === Logistics__SchedulingOptionEnum.ClutterWillContactCarrier &&
                    'Clutter will reach out to carrier to arrange a date'}
                  {load.schedulingOption === Logistics__SchedulingOptionEnum.SoonestAvailableNoRushFee &&
                    'Soonest available date that will not incur a rush fee'}
                  {load.schedulingOption === Logistics__SchedulingOptionEnum.SoonestAvailableRushFeeOk &&
                    'Soonest available date, even if that means paying a rush fee'}
                </>
              )}
            </td>
            <td className="col-sm-3 text-left" style={{ whiteSpace: 'pre-wrap' }}>
              <ExpectationsSummary load={load} />
            </td>
            <td className="col-sm-1 text-left">{load.carrierDetails?.businessName}</td>
            <td className="col-sm-2 text-right">
              <a style={buttonPadding} className="btn btn-primary" href={logisticsDetailedLoadURL({ id: load.id })}>
                View
              </a>
              <Permission resource={LOGISTICS.resource} action={LOGISTICS.actions.update} denied={null}>
                <a style={buttonPadding} className="btn btn-primary" href={logisticsEditLoadURL({ id: load.id })}>
                  Edit
                </a>
              </Permission>
            </td>
          </tr>
        ))}
      </>
    </tbody>
  </Table>
);
